import { motion, useScroll, useTransform, type Variants } from "framer-motion";
import { ArrowRight, CheckCircle2, Users, Building2, Award, Sparkles, Play } from "lucide-react";
import { Button } from "../ui/button";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

const stats = [
    { value: "5K+", label: "Active Members", icon: Users },
    { value: "10+", label: "City Partners", icon: Building2 },
    { value: "100+", label: "Events Hosted", icon: Award },
];

const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2,
        },
    },
};

const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
            ease: [0.4, 0, 0.2, 1] as const,
        },
    },
};

export const HeroSection = () => {
    const navigate = useNavigate();
    const sectionRef = useRef<HTMLElement>(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });

    const y = useTransform(scrollYProgress, [0, 1], [0, 100]);
    const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

    const scrollToSection = (href: string) => {
        const element = document.querySelector(href);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <section ref={sectionRef} className="relative pt-24 pb-12 md:pt-32 md:pb-20">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    style={{ y, opacity }}
                    className="absolute top-20 left-10 w-72 h-72 bg-accent/10 rounded-full blur-3xl"
                />
                <motion.div
                    style={{ y, opacity }}
                    className="absolute top-40 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
                />
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                    className="absolute -top-20 -right-20 w-80 h-80 border border-accent/10 rounded-full"
                />
                <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
                    className="absolute -bottom-40 -left-20 w-96 h-96 border border-primary/5 rounded-full"
                />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Left Content */}
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="text-left"
                    >
                        {/* Badge */}
                        <motion.div variants={itemVariants} className="mb-6">
                            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 backdrop-blur-sm">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
                                </span>
                                <span className="text-sm font-semibold text-foreground">
                                    #1 Civic Engagement Platform
                                </span>
                                <Sparkles className="h-4 w-4 text-accent" />
                            </div>
                        </motion.div>

                        {/* Headline */}
                        <motion.h1
                            variants={itemVariants}
                            className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.1] tracking-tight mb-4"
                        >
                            Build Stronger{" "}
                            <span className="gradient-text">Communities</span>{" "}
                            Together
                        </motion.h1>

                        {/* Subtitle */}
                        <motion.p
                            variants={itemVariants}
                            className="text-base md:text-lg text-muted-foreground max-w-xl mb-5 leading-relaxed"
                        >
                            CiviCircle connects city employees, retired professionals, and sponsors
                            to create meaningful civic impact. Join thousands transforming their communities.
                        </motion.p>

                        {/* Feature bullets */}
                        <motion.div variants={itemVariants} className="flex flex-wrap gap-x-4 gap-y-2 mb-6">
                            {["Easy to get started", "Free for members", "Trusted by cities"].map((feature, idx) => (
                                <motion.div
                                    key={feature}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.6 + idx * 0.1 }}
                                    className="flex items-center gap-2 text-sm font-medium text-foreground"
                                >
                                    <div className="w-5 h-5 rounded-full bg-accent/15 flex items-center justify-center">
                                        <CheckCircle2 className="h-3 w-3 text-accent" />
                                    </div>
                                    {feature}
                                </motion.div>
                            ))}
                        </motion.div>

                        {/* CTA Buttons */}
                        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-3">
                            <Button
                                size="lg"
                                className="group relative overflow-hidden bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 h-12 text-sm sm:text-base font-semibold rounded-xl shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
                                onClick={() => scrollToSection("#get-started")}
                            >
                                <span className="relative z-10 flex items-center">
                                    Get Started Free
                                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                                </span>
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-accent to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                />
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                className="group h-12 px-6 sm:px-8 text-sm sm:text-base font-semibold rounded-xl border-2 border-border hover:border-primary/30 hover:bg-primary/5 transition-all duration-300"
                                onClick={() => navigate("/")}
                            >
                                <Play className="mr-2 h-4 w-4 text-primary group-hover:scale-110 transition-transform" />
                                Watch Demo
                            </Button>
                        </motion.div>
                    </motion.div>

                    {/* Right Side - Stats Cards */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
                        className="relative py-8 lg:py-6"
                    >
                        {/* Floating badge - top left */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 1.1 }}
                            className="absolute top-0 left-0 lg:-left-2 glass-card-strong rounded-xl p-3 z-10 flex items-center gap-2 shadow-lg"
                        >
                            <div className="w-8 h-8 rounded-lg bg-success/15 flex items-center justify-center">
                                <CheckCircle2 className="h-4 w-4 text-success" />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-foreground">Verified</p>
                                <p className="text-[10px] text-muted-foreground">Government Partner</p>
                            </div>
                        </motion.div>

                        {/* Main Card */}
                        <div className="relative glass-card-premium rounded-2xl p-5 sm:p-6 mt-8">
                            {/* Stats Grid */}
                            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
                                {stats.map((stat, index) => (
                                    <motion.div
                                        key={stat.label}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                                        className="text-center p-2 sm:p-3 rounded-xl bg-muted/50 hover:bg-muted/70 transition-colors duration-300 group cursor-default"
                                    >
                                        <div className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-0.5 group-hover:scale-105 transition-transform">
                                            {stat.value}
                                        </div>
                                        <div className="text-[10px] sm:text-xs text-muted-foreground font-medium leading-tight">
                                            {stat.label}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Feature Icons */}
                            <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                {[
                                    { icon: Users, label: "Network", color: "accent" },
                                    { icon: Building2, label: "Collaborate", color: "primary" },
                                    { icon: Award, label: "Achieve", color: "accent" },
                                ].map((item, idx) => (
                                    <motion.div
                                        key={item.label}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.4, delay: 0.9 + idx * 0.1 }}
                                        whileHover={{ y: -3 }}
                                        className={`flex flex-col items-center p-3 rounded-xl cursor-default transition-all duration-300 ${item.color === "accent"
                                            ? "bg-accent/5 border border-accent/15 hover:bg-accent/10"
                                            : "bg-primary/5 border border-primary/15 hover:bg-primary/10"
                                            }`}
                                    >
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-1.5 ${item.color === "accent" ? "bg-accent/15" : "bg-primary/15"
                                            }`}>
                                            <item.icon className={`h-4 w-4 ${item.color === "accent" ? "text-accent" : "text-primary"}`} />
                                        </div>
                                        <span className="text-xs font-semibold text-foreground">{item.label}</span>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Glow effects */}
                            <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent/15 rounded-full blur-2xl pointer-events-none" />
                            <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
                        </div>

                        {/* Floating badge - bottom right */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, y: -20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 1.2 }}
                            className="absolute bottom-0 right-0 lg:-right-2 glass-card-strong rounded-xl p-3 z-10 flex items-center gap-2 shadow-lg"
                        >
                            <div className="flex -space-x-1.5">
                                {[1, 2, 3].map((i) => (
                                    <div
                                        key={i}
                                        className="w-7 h-7 rounded-full border-2 border-card flex items-center justify-center text-[10px] font-bold"
                                        style={{
                                            background: i === 1 ? "hsl(var(--accent) / 0.3)" : i === 2 ? "hsl(var(--primary) / 0.3)" : "hsl(var(--accent) / 0.5)",
                                        }}
                                    >
                                        {i === 1 ? "JK" : i === 2 ? "SM" : "AR"}
                                    </div>
                                ))}
                            </div>
                            <div>
                                <p className="text-xs font-bold text-foreground">+2.5k</p>
                                <p className="text-[10px] text-muted-foreground">This month</p>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
